/**
 * The A–G scale, as the map paints it.
 *
 * These hexes are the ones frontend/legacy/index.html used, not the ones in
 * tokens.css: the map's palette is older and more saturated than the funnel's,
 * and unifying them here would silently restyle every building in the Comunitat.
 * The authoritative copy lives in backend/energy.py LETTER_COLORS and is served
 * by /api/v1/letter-scale — this duplicate exists only because MapLibre paint
 * expressions have to be built before any fetch resolves.
 */

export const LETTERS = ["A", "B", "C", "D", "E", "F", "G"] as const;

export type Letter = (typeof LETTERS)[number];

export const LETTER_COLORS: Record<Letter, string> = {
  A: "#1a7340",
  B: "#4caf50",
  C: "#8bc34a",
  D: "#ffeb3b",
  E: "#ff9800",
  F: "#f44336",
  G: "#b71c1c",
};

/** Grey for a building the pipeline could not classify. */
export const LETTER_NONE = "#9e9e9e";

/** Dark text on the light-background letters, white elsewhere. */
export const LETTER_TEXT: Record<Letter, string> = {
  A: "#fff", B: "#fff", C: "#333", D: "#333", E: "#fff", F: "#fff", G: "#fff",
};

export const DEADLINES = [2030, 2033, 2035] as const;

/** Mirrors backend/energy.py EED_DEADLINES. */
export const DEADLINE_LABELS: Record<number, string> = {
  2030: "F-G",
  2033: "E",
  2035: "D",
};

/**
 * Which letters a deadline year covers.
 *
 * Cumulative, matching backend/domain/buildings.py letters_for_deadline: a
 * building due by 2033 is one rated E *or worse*, because F and G were already
 * due in 2030. Reading it as "exactly E" would hide the worst buildings from the
 * 2033 filter — the ones most likely to be why someone applied it.
 */
export function lettersForDeadline(year: number): Set<Letter> {
  if (year === 2030) return new Set(["F", "G"]);
  if (year === 2033) return new Set(["E", "F", "G"]);
  if (year === 2035) return new Set(["D", "E", "F", "G"]);
  return new Set();
}

export function letterColor(letter: string | null | undefined): string {
  return letter && letter in LETTER_COLORS
    ? (LETTER_COLORS[letter as Letter] as string)
    : LETTER_NONE;
}

export function isLetter(value: unknown): value is Letter {
  return typeof value === "string" && (LETTERS as readonly string[]).includes(value);
}
